import type { Mentionable } from '../../lib/comment-types';

/** `@[uid]` tokens of a message body (spec F.1). */
export const MENTION_RE = /@\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/g;

export function mentionIds(body: string): string[] {
  return [...new Set([...body.matchAll(MENTION_RE)].map((m) => m[1]!))];
}

export type BodySegment = { text: string } | { mention: string; name: string | null };

/** A body as text runs and mentions, for rendering as text nodes (never HTML). */
export function bodySegments(body: string, names: ReadonlyMap<string, string>): BodySegment[] {
  const out: BodySegment[] = [];
  let last = 0;
  for (const m of body.matchAll(MENTION_RE)) {
    if (m.index > last) out.push({ text: body.slice(last, m.index) });
    out.push({ mention: m[1]!, name: names.get(m[1]!) ?? null });
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push({ text: body.slice(last) });
  return out;
}

const QUERY_MAX = 40;

/** Start of the `@query` being typed at `caret`: an `@` at the start or after a blank, no blank since. */
function queryStart(text: string, caret: number): number | null {
  const at = text.lastIndexOf('@', caret - 1);
  if (at < 0 || caret - at - 1 > QUERY_MAX) return null;
  if (at > 0 && !/\s/.test(text[at - 1]!)) return null;
  if (/\s/.test(text.slice(at + 1, caret))) return null;
  return at;
}

/** The text typed after `@` at the caret (possibly empty), or null when the caret is not in a mention. */
export function mentionQuery(text: string, caret: number): string | null {
  const at = queryStart(text, caret);
  return at === null ? null : text.slice(at + 1, caret);
}

/** Replaces the `@query` at the caret with the user's token and a space; the caret goes after it. */
export function insertMention(text: string, caret: number, user: Mentionable): { text: string; caret: number } {
  const at = queryStart(text, caret) ?? caret;
  const token = `@[${user.userId}] `;
  return { text: text.slice(0, at) + token + text.slice(caret), caret: at + token.length };
}
